import { useMemo } from "react";
import { useRecoilValue } from "recoil";

import { usePageContext } from "@ryde/redux";
import {
  IncidentStatusEnum,
  liveMapGridToolbarStatsAtomFamily,
  liveMapNewIncidentsAF,
  RideDelayStatusEnum,
  selectedLiveMapMapRidesSF,
  sortedRideDelayStatus,
} from "@ryde/shared";

export interface ILiveMapGridStatusCount {
  status: RideDelayStatusEnum;
  count: number;
}

interface IUseLiveMapToolbarStatsResult {
  gridStatusCounts: ILiveMapGridStatusCount[];
  mapRidesCount: number;
  mapSeatsCount: number;
  mapDisplayedIncidentsCount: number;
  plannedForNowCount: number;
  newIncidentsTotal: number;
}

export const useLiveMapToolbarStats = (): IUseLiveMapToolbarStatsResult => {
  const { entity } = usePageContext();
  const serverStats = useRecoilValue(liveMapGridToolbarStatsAtomFamily(entity));
  const mapRides = useRecoilValue(selectedLiveMapMapRidesSF(entity));
  const polledIncidents = useRecoilValue(liveMapNewIncidentsAF(entity));

  const gridStatusCounts = useMemo((): ILiveMapGridStatusCount[] => {
    return sortedRideDelayStatus
      .map(status => ({
        status,
        count: serverStats.countsByDelayStatus[status] ?? 0,
      }))
      .filter(({ count }) => count > 0);
  }, [serverStats.countsByDelayStatus]);

  const mapRideIds = useMemo(
    () => new Set(mapRides.map(ride => ride.id)),
    [mapRides],
  );

  const mapSeatsCount = useMemo(
    () =>
      mapRides.reduce(
        (sum, ride) => sum + Number(ride.metadata?.seatsCount ?? 0),
        0,
      ),
    [mapRides],
  );

  const mapDisplayedIncidentsCount = useMemo(() => {
    if (!mapRideIds.size) {
      return 0;
    }

    return polledIncidents.filter(incident => {
      const rideId = incident.rideId;
      return (
        incident.status === IncidentStatusEnum.New &&
        typeof rideId === "number" &&
        mapRideIds.has(rideId)
      );
    }).length;
  }, [mapRideIds, polledIncidents]);

  return {
    gridStatusCounts,
    mapRidesCount: mapRides.length,
    mapSeatsCount,
    mapDisplayedIncidentsCount,
    plannedForNowCount: serverStats.plannedForNowCount,
    newIncidentsTotal: serverStats.newIncidentsTotal,
  };
};
